import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Fade,
  Divider,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MedicalInformationIcon from '@mui/icons-material/MedicalInformation';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import PersonIcon from '@mui/icons-material/Person';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StethoscopeIcon from '@mui/icons-material/MonitorHeart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Link as RouterLink } from 'react-router-dom';
import BrandMark from '../components/BrandMark';

// ── Mockup de agenda (hero) — bloques de colores reales del calendario,
// sin captura real todavía: fácil de reemplazar por una <img> más adelante
// sin tocar el resto de la página. ──────────────────────────────────────
const AgendaMockup = () => {
  const rows = [
    { time: '09:00', name: 'M. Fernández', color: '#ffb300' },
    { time: '09:30', name: 'J. Pérez', color: '#1565c0' },
    { time: '10:00', name: '— libre —', color: '#9e9e9e', faded: true },
    { time: '10:30', name: 'L. Gómez', color: '#ef5350' },
    { time: '11:00', name: 'R. Suárez', color: '#1565c0' },
  ];
  return (
    <Box sx={{
      bgcolor: 'background.paper', borderRadius: 3, p: 2, boxShadow: 8,
      maxWidth: 360, mx: 'auto', textAlign: 'left',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.primary">Hoy — Dra. Lopez</Typography>
        <Chip label="5 turnos" size="small" color="primary" variant="outlined" />
      </Box>
      {rows.map((r, i) => (
        <Box key={i} sx={{
          display: 'flex', alignItems: 'center', gap: 1.25, py: 0.9,
          borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider',
          opacity: r.faded ? 0.5 : 1,
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ width: 42, flexShrink: 0, fontWeight: 600 }}>
            {r.time}
          </Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: r.color, flexShrink: 0 }} />
          <Typography variant="body2" color="text.primary" noWrap>{r.name}</Typography>
        </Box>
      ))}
    </Box>
  );
};

const STEPS = [
  {
    icon: <HowToRegIcon sx={{ fontSize: 32 }} />,
    title: 'Registrá tu clínica',
    text: 'Creás tu cuenta de admin en 2 minutos, sin tarjeta ni instalación.',
  },
  {
    icon: <PersonSearchIcon sx={{ fontSize: 32 }} />,
    title: 'Cargá médicos y pacientes',
    text: 'Agregás tu equipo y empezás a sumar pacientes a medida que llegan.',
  },
  {
    icon: <EventAvailableIcon sx={{ fontSize: 32 }} />,
    title: 'Agendá turnos',
    text: 'Calendario visual por médico, con recordatorios automáticos por email y WhatsApp.',
  },
  {
    icon: <NotificationsActiveIcon sx={{ fontSize: 32 }} />,
    title: 'Listo, el día a día funciona solo',
    text: 'Sala de espera, recetas, facturación y métricas, todo conectado entre sí.',
  },
];

const FEATURE_GROUPS = [
  {
    icon: <CalendarTodayIcon color="primary" sx={{ fontSize: 44 }} />,
    title: 'Agenda y turnos',
    items: [
      'Calendario visual por médico, con vista día/semana',
      'Recordatorios automáticos por email y WhatsApp',
      'Sala de espera y lista de espera para turnos liberados',
      'Tipos de turno con colores y duración propia',
    ],
  },
  {
    icon: <FolderSharedIcon color="primary" sx={{ fontSize: 44 }} />,
    title: 'Pacientes e historia clínica',
    items: [
      'Fichas completas con antecedentes, alergias y medicación',
      'Historia clínica estructurada por consulta, con plantillas',
      'Recetas y órdenes de estudio con firma digital y QR de verificación',
      'Adjuntos de archivos y estudios (imágenes, PDF)',
    ],
  },
  {
    icon: <ReceiptLongIcon color="primary" sx={{ fontSize: 44 }} />,
    title: 'Facturación y métricas',
    items: [
      'Cobros por consulta, seguimiento de señas y saldos',
      'Exportación a Excel de turnos, pacientes y facturación',
      'Métricas de ocupación, revenue y actividad en tiempo real',
      'Auditoría completa de cada acción en el sistema',
    ],
  },
  {
    icon: <AccountTreeIcon color="primary" sx={{ fontSize: 44 }} />,
    title: 'Módulos adicionales',
    items: [
      'Algoritmos clínicos interactivos para residentes',
      'Portal del paciente para ver y cancelar turnos sin llamar',
      'Autocompletado de datos del paciente por link, sin login',
      'Instalable como app en el celular, con ícono propio',
    ],
  },
];

const ROLES = [
  {
    icon: <SupportAgentIcon color="primary" sx={{ fontSize: 40 }} />,
    role: 'Recepción',
    text: 'Agenda turnos en un click, gestiona la sala de espera y manda recordatorios sin complicarse.',
  },
  {
    icon: <StethoscopeIcon color="primary" sx={{ fontSize: 40 }} />,
    role: 'Médicos',
    text: 'Historia clínica con autoguardado, recetas digitales y calculadoras clínicas a mano.',
  },
  {
    icon: <PersonIcon color="primary" sx={{ fontSize: 40 }} />,
    role: 'Dueños de clínica',
    text: 'Métricas de ocupación y facturación, usuarios y permisos, todo desde un solo panel.',
  },
];

const PLANS = [
  {
    name: 'Free',
    price: 'Gratis',
    highlight: false,
    description: 'Para empezar a usar el sistema sin costo.',
    items: [
      'Hasta 2 médicos',
      'Agenda, turnos y pacientes sin límite',
      'Historia clínica, facturación y métricas',
      'Portal del paciente y autocompletado de datos',
    ],
  },
  {
    name: 'Pro',
    price: 'A medida',
    highlight: true,
    description: 'Para clínicas que necesitan más médicos y módulos avanzados.',
    items: [
      'Médicos sin límite',
      'Todo lo del plan Free',
      'Recetas electrónicas con firma digital',
      'Algoritmos clínicos interactivos',
    ],
  },
];

const SectionTitle = ({ eyebrow, title, subtitle }) => (
  <Box sx={{ textAlign: 'center', mb: { xs: 4, md: 6 } }}>
    {eyebrow && (
      <Typography variant="overline" color="primary.main" fontWeight={700} sx={{ letterSpacing: 1.5 }}>
        {eyebrow}
      </Typography>
    )}
    <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>{title}</Typography>
    {subtitle && (
      <Typography color="text.secondary" sx={{ maxWidth: 600, mx: 'auto', mt: 1.5 }}>
        {subtitle}
      </Typography>
    )}
  </Box>
);

const Home = () => {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Hero */}
      <Box
        sx={{
          background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
          color: 'white',
          pt: { xs: 7, md: 10 },
          pb: { xs: 7, md: 12 },
        }}
      >
        <Box sx={{ maxWidth: 1180, mx: 'auto', px: { xs: 2, md: 4 } }}>
          <Grid container spacing={{ xs: 5, md: 4 }} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Fade in timeout={800}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <BrandMark size={44} variant="bare" />
                  <Typography variant="h5" fontWeight={700}>VitaNotas</Typography>
                </Box>
              </Fade>
              <Fade in timeout={1100}>
                <Typography variant="h3" fontWeight={800} sx={{ fontSize: { xs: '2.1rem', md: '2.8rem' }, lineHeight: 1.15, mb: 2 }}>
                  Gestioná tu clínica sin planillas ni WhatsApp sueltos
                </Typography>
              </Fade>
              <Fade in timeout={1400}>
                <Typography variant="h6" sx={{ opacity: 0.92, fontWeight: 400, mb: 4, maxWidth: 480 }}>
                  Turnos, pacientes, historia clínica, recetas y facturación en un solo lugar.
                  Pensado para consultorios y clínicas pequeñas.
                </Typography>
              </Fade>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  size="large"
                  color="inherit"
                  sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 700, '&:hover': { bgcolor: 'grey.100' } }}
                  component={RouterLink}
                  to="/register"
                >
                  Registrar mi clínica
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  color="inherit"
                  component={RouterLink}
                  to="/login"
                >
                  Iniciar sesión
                </Button>
              </Box>
              <Typography variant="body2" sx={{ mt: 3, opacity: 0.85 }}>
                ¿Sos paciente de una clínica que usa VitaNotas?{' '}
                <RouterLink to="/portal" style={{ color: 'inherit', fontWeight: 600 }}>
                  Accedé a tus turnos
                </RouterLink>
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Fade in timeout={1700}>
                <Box>
                  <AgendaMockup />
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Cómo funciona */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <SectionTitle eyebrow="Cómo funciona" title="De cero a funcionando en el día" />
          <Grid container spacing={3}>
            {STEPS.map((s, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Box sx={{ textAlign: 'center', position: 'relative' }}>
                  <Box sx={{
                    width: 72, height: 72, borderRadius: '50%', bgcolor: 'primary.50',
                    color: 'primary.main', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', mx: 'auto', mb: 2,
                  }}>
                    {s.icon}
                  </Box>
                  <Chip label={`Paso ${i + 1}`} size="small" color="primary" sx={{ mb: 1, fontWeight: 700 }} />
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>{s.title}</Typography>
                  <Typography variant="body2" color="text.secondary">{s.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Features completas por grupo */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
        <Box sx={{ maxWidth: 1180, mx: 'auto' }}>
          <SectionTitle
            eyebrow="Todo incluido"
            title="Todo lo que tu consultorio necesita"
            subtitle="Un solo sistema para la agenda, la historia clínica y la parte administrativa — sin planillas sueltas ni WhatsApp para coordinar."
          />
          <Grid container spacing={3}>
            {FEATURE_GROUPS.map((g, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Card elevation={0} sx={{ height: '100%', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ mb: 1.5 }}>{g.icon}</Box>
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>{g.title}</Typography>
                    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                      {g.items.map((item, j) => (
                        <Box component="li" key={j} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                          <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main', mt: '3px', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary">{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Mini destacados — PWA + QR, para mostrarlos con algo más de
              peso visual que una viñeta más dentro de la tarjeta de arriba. */}
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2.5, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <PhoneIphoneIcon color="primary" sx={{ fontSize: 36, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Se instala como app</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Desde el celular, con su propio ícono — sin pasar por ninguna tienda de aplicaciones.
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2.5, bgcolor: 'background.paper', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <QrCode2Icon color="primary" sx={{ fontSize: 36, flexShrink: 0 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>Recetas verificables por QR</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Cada receta emitida tiene firma digital y un QR que cualquiera puede verificar públicamente.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Para quién es */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 4 } }}>
        <Box sx={{ maxWidth: 1000, mx: 'auto' }}>
          <SectionTitle eyebrow="Para quién es" title="Pensado para todo el equipo de la clínica" />
          <Grid container spacing={3}>
            {ROLES.map((r, i) => (
              <Grid size={{ xs: 12, sm: 4 }} key={i}>
                <Box sx={{ textAlign: 'center', p: 2 }}>
                  <Box sx={{ mb: 1.5 }}>{r.icon}</Box>
                  <Typography variant="subtitle1" fontWeight={700} gutterBottom>{r.role}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.text}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* Planes */}
      <Box sx={{ py: { xs: 7, md: 10 }, px: { xs: 2, md: 4 }, bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.900' : 'grey.50' }}>
        <Box sx={{ maxWidth: 820, mx: 'auto' }}>
          <SectionTitle eyebrow="Planes" title="Empezá gratis, crecé cuando lo necesites" />
          <Grid container spacing={3} justifyContent="center">
            {PLANS.map((p, i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={i}>
                <Card
                  elevation={p.highlight ? 6 : 0}
                  sx={{
                    height: '100%', borderRadius: 3, p: 1,
                    border: p.highlight ? 'none' : '1px solid',
                    borderColor: 'divider',
                    ...(p.highlight && { outline: '2px solid', outlineColor: 'primary.main' }),
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="h6" fontWeight={700}>{p.name}</Typography>
                      {p.highlight && <Chip label="Más completo" size="small" color="primary" />}
                    </Box>
                    <Typography variant="h4" fontWeight={800} color={p.highlight ? 'primary.main' : 'text.primary'} sx={{ mb: 0.5 }}>
                      {p.price}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                      {p.description}
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                      {p.items.map((item, j) => (
                        <Box component="li" key={j} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.25 }}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main', mt: '1px', flexShrink: 0 }} />
                          <Typography variant="body2">{item}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
            ¿Necesitás más de 2 médicos o los módulos avanzados? Escribinos al registrarte y te pasamos a Pro.
          </Typography>
        </Box>
      </Box>

      {/* Solicitar Acceso */}
      <Box id="solicitar" sx={{
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
        color: 'white', p: { xs: 6, md: 10 }, textAlign: 'center',
      }}>
        <MedicalInformationIcon sx={{ fontSize: 48, opacity: 0.9, mb: 2 }} />
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Empezá hoy mismo
        </Typography>
        <Typography sx={{ mb: 5, maxWidth: 600, mx: 'auto', opacity: 0.92 }}>
          Registrá tu clínica y empezá a usarla en minutos. Sin costo inicial, sin compromiso.
        </Typography>

        <Button
          variant="contained"
          size="large"
          color="inherit"
          component={RouterLink}
          to="/register"
          sx={{ bgcolor: 'white', color: 'primary.main', fontWeight: 700, py: 1.75, px: 6, '&:hover': { bgcolor: 'grey.100' } }}
        >
          Registrarme gratis
        </Button>
      </Box>

      {/* Footer */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 4, textAlign: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
          <BrandMark size={22} variant="bare" />
          <Typography variant="body2" fontWeight={700}>VitaNotas</Typography>
        </Box>
        <Typography variant="body2">
          © 2026 VitaNotas – Gestión simple para consultorios médicos
        </Typography>
        <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.85 }}>
          ¿Sos paciente?{' '}
          <RouterLink to="/portal" style={{ color: 'inherit' }}>
            Accedé a tus turnos
          </RouterLink>
        </Typography>
        <Typography variant="caption" sx={{ mt: 2, display: 'block', opacity: 0.7, maxWidth: 640, mx: 'auto' }}>
          VitaNotas es una herramienta de gestión administrativa para consultorios y clínicas.
          No brinda diagnóstico, tratamiento ni consejo médico, y no reemplaza la atención
          profesional de un médico. Ante una urgencia, comunicate directamente con tu clínica o
          con el servicio de emergencias correspondiente.
        </Typography>
      </Box>
    </Box>
  );
};

export default Home;
